import * as Sentry from '@sentry/react';

/**
 * Recovery for stale lazy-loaded bundles.
 *
 * Every deploy publishes chunks under fresh content hashes and drops the previous ones. A
 * tab opened before the deploy still holds the old chunk names, so the first lazy import
 * after a release fails — the request either 404s or, when the host rewrites unknown paths
 * to the SPA shell, returns `index.html` and the parser reports `Unexpected token '<'`.
 *
 * Neither is a code defect: the fix is to reload and pick up the current index.html. Reload
 * once, and only report if the error survives that, because a chunk that is still
 * unreachable after a reload is a real problem (bad deploy, poisoned cache, blocked CDN).
 */

const RELOAD_MARKER = 'hf:chunk-reload-at';
const RELOAD_COOLDOWN_MS = 30000;

/** Matches our webpack output: `js/[name]-bundle-[contenthash].js` and the CSS equivalent. */
const BUNDLE_URL_PATTERN = /\/(?:js|css)\/[^/]+\.(?:js|css)$/i;

/** Firefox and Chrome word "this JS file is actually HTML" differently. */
export const HTML_AS_SCRIPT_PATTERN = /Unexpected token\s*'?</i;

const CHUNK_LOAD_PATTERN = /Loading (?:CSS )?chunk [^\s]+ failed/i;

/**
 * Our bundles always come from the document's own origin: webpack leaves `output.publicPath`
 * at `auto` and the host serves `public/` alongside `index.html`.
 *
 * Third-party scripts do not, and several of them sit under a path the pattern above matches
 * happily — Google Charts pulls `www.gstatic.com/charts/51/js/*.js`, Beamer pulls
 * `app.getbeamer.com/js/beamer-embed.js`. Mistaking one of those for a stale chunk reloads
 * the page over an asset that no deploy of ours can fix, and then files the second failure
 * as our defect. Hence the origin check: a cross-origin asset is never our chunk.
 */
export const isBundleUrl = (url?: string | null): boolean => {
    if (!url || typeof window === 'undefined') {
        return false;
    }

    try {
        const parsed = new URL(url, window.location.href);
        return parsed.origin === window.location.origin && BUNDLE_URL_PATTERN.test(parsed.pathname);
    } catch {
        // Not something resolvable to a URL (`<anonymous>`, `eval`, a native frame). Not ours.
        return false;
    }
};

/**
 * Allow one reload per cooldown window. Without the marker a permanently broken chunk would
 * reload the page forever; without a cooldown a user could never recover from two deploys
 * in quick succession.
 */
const claimReload = (): boolean => {
    try {
        const lastReload = Number(window.sessionStorage.getItem(RELOAD_MARKER) ?? 0);
        if (Number.isFinite(lastReload) && Date.now() - lastReload < RELOAD_COOLDOWN_MS) {
            return false;
        }
        window.sessionStorage.setItem(RELOAD_MARKER, String(Date.now()));
        return true;
    } catch {
        // Storage disabled (private mode, blocked cookies). Reloading blind risks a loop.
        return false;
    }
};

export const setupChunkRecovery = (): void => {
    if (typeof window === 'undefined') {
        return;
    }

    const recover = (reason: string, detail: Record<string, unknown>): void => {
        if (claimReload()) {
            window.location.reload();
            return;
        }

        Sentry.captureException(new Error(`Chunk load failed after reload: ${reason}`), {
            tags: { type: 'ChunkLoadError' },
            fingerprint: ['chunk-load-failed', reason],
            extra: detail
        });
    };

    // Capture phase: resource load errors fire on the element and do not bubble.
    window.addEventListener('error', (event: Event) => {
        const element = event.target as HTMLElement | null;

        if (element && (element.tagName === 'SCRIPT' || element.tagName === 'LINK')) {
            const assetUrl = (element as HTMLScriptElement).src || (element as HTMLLinkElement).href;
            if (isBundleUrl(assetUrl)) {
                recover('asset request failed', { assetUrl });
            }
            return;
        }

        const errorEvent = event as ErrorEvent;
        if (isBundleUrl(errorEvent.filename) && HTML_AS_SCRIPT_PATTERN.test(errorEvent.message ?? '')) {
            recover('bundle served as HTML', {
                filename: errorEvent.filename,
                message: errorEvent.message
            });
        }
    }, true);

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        const reason = event.reason as { name?: string; message?: string; request?: string } | undefined;
        if (!reason) {
            return;
        }

        const message = reason.message ?? '';
        if (reason.name === 'ChunkLoadError' || CHUNK_LOAD_PATTERN.test(message)) {
            recover('dynamic import rejected', { message, request: reason.request });
        }
    });
};
